import Header from "../components/header";
import mainStore from "../../store/store";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/footer";
import Stepper from "../components/stepper";
import moment from "moment";
import { useLocation } from "react-router-dom";
import axios from "axios";
import axiosInstance from "../../axios-interceptor/axios";
import { SkeletonCarsList } from "../components/skeleton";
import "../../styles/carCards.css";

// Simple inline AvailabilityModal component
const AvailabilityModal = ({ isOpen, onClose, car, upcomingReturns }) => {
  if (!isOpen) return null;

  // Calculate booked periods to show (current month + next month)
  const now = moment();
  const endOfNextMonth = moment().add(1, 'month').endOf('month');
  
  // Filter returns to only show current month + next month
  const filteredReturns = (upcomingReturns || []).filter(r => {
    const returnDate = moment(r.returnDate);
    return returnDate.isSameOrBefore(endOfNextMonth);
  });

  // Find the last return date to show "From X onwards"
  const lastReturn = filteredReturns.length > 0 
    ? moment(filteredReturns[filteredReturns.length - 1].returnDate)
    : null;
  
  return (
    <div className="availability-modal-overlay" onClick={onClose}>
      <div className="availability-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="availability-modal-header">
          <div>
            <h3>{car?.name}</h3>
            <p>When this vehicle is available</p>
          </div>
          <button className="availability-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="availability-modal-body">
          <p className="availability-subtitle">
            This vehicle is currently booked for your selected dates. You can rent it during these periods:
          </p>
          {filteredReturns.length > 0 ? (
            <div className="availability-periods">
              {filteredReturns.map((returnInfo, index) => {
                const returnDate = moment(returnInfo.returnDate);
                const daysFromNow = returnDate.diff(now, 'days');
                const daysLabel = daysFromNow <= 0 ? 'Today' : `${daysFromNow} days`;
                
                return (
                  <div key={index} className="availability-period-card">
                    <div className="availability-period-info">
                      <span className="availability-period-dates">
                        {returnDate.format('D MMM YYYY')} — {returnDate.add(7, 'days').format('D MMM YYYY')}
                      </span>
                      <span className="availability-period-days">{daysLabel}</span>
                    </div>
                  </div>
                );
              })}
              {lastReturn && (
                <div className="availability-period-card availability-period-available">
                  <div className="availability-period-info">
                    <span className="availability-period-dates">
                      From {lastReturn.add(1, 'day').format('D MMM YYYY')} onwards
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="availability-empty">
              No upcoming booking information available. Please contact us for availability.
            </p>
          )}
        </div>
        <div className="availability-modal-footer">
          <p className="availability-contact-text">Need help? Get in touch with us:</p>
          <div className="availability-contact-buttons">
            <a href="tel:+61893415334" className="availability-btn-call">+61 893 415 334</a>
            <a href="mailto:info@tak8.com.au" className="availability-btn-email">info@tak8.com.au</a>
          </div>
          <button className="availability-btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const Cars = () => {
  const [loading, setLoading] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedCarForAvailability, setSelectedCarForAvailability] = useState(null);
  const [availabilityData, setAvailabilityData] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const {
    getAllCars,
    userBooking,
    setUserBooking,
    visitedRoutes,
    setVisitedRoutes,
    getCarImg,
    cachedCars,
    setCachedCars,
  } = mainStore();
  const [allCarsList, setAllCarsList] = useState(cachedCars.length > 0 ? cachedCars : []);

  const fromfind = searchParams.get("find");
  console.log("fromfind", fromfind);

  useEffect(() => {
    // SEO Meta Tags
    document.title =
      "Best Rental Cars in Perth | Hatchbacks, Sedans, SUVs – TAK8";

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Find the best rental cars in Perth with TAK8. Our fleet of late-model hatchbacks, sedans, and SUVs is perfect for city driving or weekend adventures."
      );
    }

    // Set or create Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute("content", "Wide Range of Cars for Hire – TAK8 Perth");

    let ogDescription = document.querySelector(
      'meta[property="og:description"]'
    );
    if (!ogDescription) {
      ogDescription = document.createElement("meta");
      ogDescription.setAttribute("property", "og:description");
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute(
      "content",
      "Explore our Perth rental fleet, from compact cars to large SUVs. We have a car for every need."
    );

    let ogType = document.querySelector('meta[property="og:type"]');
    if (!ogType) {
      ogType = document.createElement("meta");
      ogType.setAttribute("property", "og:type");
      document.head.appendChild(ogType);
    }
    ogType.setAttribute("content", "website");

    initializeCars();
    fetchAvailabilityData();
  }, []);

  const fetchAvailabilityData = async () => {
    try {
      const response = await axiosInstance.get("/cars/admin/availability");
      if (response.data) {
        setAvailabilityData(response.data);
        console.log('✅ Availability data fetched:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching availability data:', error);
    }
  };

  const handleViewAvailability = async (car) => {
    // First try the pre-fetched availability data
    const carAvailability = availabilityData.find(
      (avail) => avail.carId === car._id
    );
    
    if (carAvailability && carAvailability.upcomingReturns && carAvailability.upcomingReturns.length > 0) {
      // Filter to only show current month + next month
      const now = new Date();
      const endOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0);
      const filteredReturns = carAvailability.upcomingReturns.filter(
        (r) => new Date(r.returnDate) <= endOfNextMonth
      );
      
      setSelectedCarForAvailability({
        ...car,
        upcomingReturns: filteredReturns.length > 0 ? filteredReturns : carAvailability.upcomingReturns.slice(0, 3),
        nextAvailableDate: carAvailability.nextAvailableDate
      });
      setShowAvailabilityModal(true);
      return;
    }

    // Fallback: fetch availability calendar directly for this car (public endpoint)
    // Limit to current month + next month
    const now = new Date();
    const fromDate = moment(now).format('YYYY-MM-DD');
    const toDate = moment(now).add(2, 'months').endOf('month').format('YYYY-MM-DD');
    
    try {
      const response = await axiosInstance.get(`/cars/${car._id}/availability-calendar?from=${fromDate}&to=${toDate}`);
      if (response.data && response.data.bookedPeriods) {
        const upcomingReturns = response.data.bookedPeriods
          .filter(period => new Date(period.to) >= new Date())
          .map(period => ({
            returnDate: period.to,
            bookingReference: period.bookingReference || 'N/A',
            customerName: ''
          }))
          .sort((a, b) => new Date(a.returnDate) - new Date(b.returnDate))
          .slice(0, 5);

        setSelectedCarForAvailability({
          ...car,
          upcomingReturns,
          nextAvailableDate: upcomingReturns.length > 0 ? upcomingReturns[0].returnDate : null
        });
        setShowAvailabilityModal(true);
      } else {
        // No booked periods data, still show modal
        setSelectedCarForAvailability({
          ...car,
          upcomingReturns: [],
          nextAvailableDate: null
        });
        setShowAvailabilityModal(true);
      }
    } catch (error) {
      console.error('Error fetching car availability calendar:', error);
      // Still show modal with whatever info we have
      setSelectedCarForAvailability({
        ...car,
        upcomingReturns: [],
        nextAvailableDate: null
      });
      setShowAvailabilityModal(true);
    }
  };

  const initializeCars = async () => {
    if (cachedCars.length === 0) {
      setLoading(true);
    }
    try {
      const updatedRoutes = [...visitedRoutes];
      updatedRoutes.push("car");
      setVisitedRoutes(updatedRoutes);
    
    // const startDate = moment(userBooking.booking.pickUpDate);
    // const endDate = moment(userBooking.booking.returnDate);
    const startDateTime = moment(
      `${userBooking.booking.pickUpDate} ${userBooking.booking.pickUpTime}`,
      "YYYY-MM-DD hh:mm A"
    );
    const endDateTime = moment(
      `${userBooking.booking.returnDate} ${userBooking.booking.returnTime}`,
      "YYYY-MM-DD hh:mm A"
    );

    const durationInHours = endDateTime.diff(startDateTime, "hours");

    const inclusiveDays =
      durationInHours < 24 ? 1 : Math.ceil(durationInHours / 24);
    userBooking.booking.days = inclusiveDays;
    userBooking.booking.driverAge = 21;
    //const inclusiveDays = endDate.diff(startDate, "days") === 0 ? 1 : endDate.diff(startDate, "days");
    // Pass user-selected dates to getAllCars so API checks only overlapping bookings
    const startDateStr = userBooking.booking.pickUpDate
      ? moment(userBooking.booking.pickUpDate).format("YYYY-MM-DD")
      : null;
    const endDateStr = userBooking.booking.returnDate
      ? moment(userBooking.booking.returnDate).format("YYYY-MM-DD")
      : null;
    const res = await getAllCars(startDateStr, endDateStr);
    if (res) {
      const bookedCars = res.map((car) => {
        const rates = car.rates;
        const unlimited_rates = car.unlimedRates;
        // car.type === "limited"
        //   ? car.rates
        //   : car.unlimedRates;

        const rate = rates.find(
          (rate) =>
            inclusiveDays >= rate.minDays &&
            (rate.maxDays === null || inclusiveDays <= rate.maxDays)
        );
        const unlimited_rate = unlimited_rates.find(
          (rate) =>
            inclusiveDays >= rate.minDays &&
            (rate.maxDays === null || inclusiveDays <= rate.maxDays)
        );
        
        // Always show DAILY rates (not multiplied by days)
        if (rate) {
          car.defaultRatePerDay = rate.rate;
          car.displayRateLabel = rate.title || `${rate.minDays}+ Days Rate`;
          // Store original price if discount was applied
          if (rate.originalRate) {
            car.originalRatePerDay = rate.originalRate;
          }
        }
        
        if (unlimited_rate) {
          car.defaultUnlimitedRatePerDay = unlimited_rate.rate;
          car.displayUnlimitedRateLabel = unlimited_rate.title || `${unlimited_rate.minDays}+ Days Rate`;
          // Store original price if discount was applied
          if (unlimited_rate.originalRate) {
            car.originalUnlimitedRatePerDay = unlimited_rate.originalRate;
          }
        }

        // Use server-provided availability
        const isUnavailable = car.availabilityStatus === 'unavailable';
        const isSoldOut = car.isFullyBooked || car.availableNow === 0;
        
        // Get the display label for unavailable cars
        let unavailableLabel = null;
        if (isUnavailable) {
          unavailableLabel = car.availabilityLabel || 'Coming Soon';
        } else if (isSoldOut) {
          unavailableLabel = 'Sold Out';
        }
        
        return {
          ...car,
          overlappingCount: car.activeBookings || 0,
          isSold: isSoldOut,
          isUnavailable,
          unavailableLabel,
        };
      });

      setAllCarsList(bookedCars);
      setCachedCars(bookedCars);
    }
      console.log("initializeCars ~ res:", res);
    } catch (error) {
      console.error("Error initializing cars:", error);
    } finally {
      setLoading(false);
    }
  };

  const onSelectingCar = (car, type) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    let booking = { ...userBooking };
    booking.car = { ...car, type: type };
    
    // Store the rate tiers with discounts already applied
    booking.selectedRates = type === "limited" ? car.rates : car.unlimedRates;
    booking.selectedType = type;
    booking.hasDiscount = !!car.activeDiscount;

    setUserBooking(booking);
    navigate(`/booking?id=${car._id}`);
  };

  if (loading) {
    return (
      <>
        <Header />
        <SkeletonCarsList />
        <Footer />
      </>
    );
  }

  // const getCarImg = (car) => {
  //   if (car.name === "Suzuki Swift") {
  //     return "img/cars/suzuki_swift.png";
  //   } else if (car.name === "Mazda CX3") {
  //     return "img/cars/mazda_cx3.png";
  //   }
  //   else if (car.name === "Mazda CX8") {
  //     return "img/cars/mazda_cx8.png";
  //   } else if (car.name === "Toyota Camry") {
  //     return "img/cars/toyota_camry.png";
  //   } else if (car.name === "Toyota Hilux") {
  //     return "img/cars/hilux.png";
  //   } else if (car.name === "Toyota Corolla") {
  //     return "img/cars/toyota_corrolla.png";
  //   } else if (car.name === "Outlander") {
  //     return "img/cars/mitsubishi_out.png";
  //   } else if (car.name === "Tesla Model 3") {
  //     return "img/cars/car_6.png";
  //   }
  // };

  return (
    <>
      <Header />
      <section class="MainSection">
        <div class="container">
          <h1 class="Head_2">Cars</h1>
        </div>
      </section>
      <section>
        <div class="container">
          <Stepper />
          <div class="row">
            {allCarsList.map((car, index) => (
              <div class="col-md-4" key={car._id || index}>
                <div class={`CarBox ${car.isUnavailable ? 'car-unavailable' : ''}`}>
                  {/* Discount Banner - Keep this */}
                  {!car.isUnavailable && car.activeDiscount && (
                    <div className="discount-banner-header">
                      <span className="discount-name">{car.activeDiscount.name}</span>
                      <span className="discount-percentage">{car.activeDiscount.badge}</span>
                    </div>
                  )}
                  <div class="Image">
                    <img src={getCarImg(car)} alt="car" />
                    {car.isAutomatic ? (
                      <span class="Tag Red">Automatic</span>
                    ) : (
                      <span class="Tag Yellow">Manual</span>
                    )}
                    {/* <span class="Tag Red" onClick={() => setPopupOpen(true)}>Details</span> */}
                    <span class="Feature">
                      <img src="img/icon/carseat.svg" alt="Icon" /> {car.seats}{" "}
                      Seats |
                      <img src="img/icon/bags.svg" alt="Icon" /> {car.bags} Bags
                    </span>
                  </div>
                  <div class="Content">
                    <div class="Heading">
                      <h4 class="Head_3">{car.name}</h4>
                      {/* <img src="img/icon/carseat.svg" alt="Icon" /> {car.seats}{" "}
                      Seats
                      <img src="img/icon/bags.svg" alt="Icon" /> {car.bags} Bags */}
                      <div class="Features">
                        {/* <img src="img/icon/air_conditioning.svg" alt="Icon" />
                        <img src="img/icon/gps_navigation.svg" alt="Icon" />
                        <img src="img/icon/bluetooth.svg" alt="Icon" />
                        <img src="img/icon/camera.svg" alt="Icon" />
                        <img src="img/icon/cruise_control.svg" alt="Icon" /> */}
                      </div>
                    </div>
                    <div className="PriceMain">
                      <div class="Price">
                        <div>
                          {car.originalRatePerDay && !car.isUnavailable && (
                            <span className="savings">Save ${(car.originalRatePerDay - car.defaultRatePerDay)}</span>
                          )}
                          {car.originalRatePerDay && !car.isUnavailable ? (
                            <>
                              <h5 className="original-price">${car.originalRatePerDay}</h5>
                              <h5 className="discounted-price">${car.defaultRatePerDay}</h5>
                            </>
                          ) : (
                            <h5>${car.defaultRatePerDay || '0.00'}</h5>
                          )}
                          <span className="rate-label">{car.displayRateLabel || "7+ Days Rate"}</span>
                        </div>

                        <p>150KM / Day</p>
                        <a
                          data-analytics-name={`Select Car - ${car.name} - Limited KM`}
                          onClick={() => {
                            if (!car.isUnavailable && !car.isSold) {
                              onSelectingCar(car, "limited");
                            }
                          }}
                          class={`BtnBorderY cursor-pointer ${
                            (car.isSold || car.isUnavailable) ? "disabled-link" : ""
                          }`}
                        >
                          {car.unavailableLabel || (car.isSold ? "Sold Out" : "Select")}
                        </a>
                        {(car.isSold || car.isUnavailable) && (
                          <span 
                            data-analytics-name={`View Availability - ${car.name} - Limited`}
                            className="view-availability-link"
                            onClick={(e) => { e.stopPropagation(); handleViewAvailability(car); }}
                          >
                            View Availability
                          </span>
                        )}
                      </div>
                      <div class="Price">
                        <div>
                          {car.originalUnlimitedRatePerDay && !car.isUnavailable && (
                            <span className="savings">Save ${(car.originalUnlimitedRatePerDay - car.defaultUnlimitedRatePerDay)}</span>
                          )}
                          {car.originalUnlimitedRatePerDay && !car.isUnavailable ? (
                            <>
                              <h5 className="original-price">${car.originalUnlimitedRatePerDay}</h5>
                              <h5 className="discounted-price">${car.defaultUnlimitedRatePerDay}</h5>
                            </>
                          ) : (
                            <h5>${car.defaultUnlimitedRatePerDay || '0.00'}</h5>
                          )}
                          <span className="rate-label">{car.displayUnlimitedRateLabel || "7+ Days Rate"}</span>
                        </div>

                        <p>Unlimited KM</p>
                        <a
                          data-analytics-name={`Select Car - ${car.name} - Unlimited KM`}
                          onClick={() => {
                            if (!car.isUnavailable && !car.isSold) {
                              onSelectingCar(car, "unlimited");
                            }
                          }}
                          class={`BtnFill cursor-pointer ${
                            (car.isSold || car.isUnavailable) ? "disabled-link" : ""
                          }`}
                        >
                          {car.unavailableLabel || (car.isSold ? "Sold Out" : "Select")}
                        </a>
                        {(car.isSold || car.isUnavailable) && (
                          <span 
                            data-analytics-name={`View Availability - ${car.name} - Unlimited`}
                            className="view-availability-link"
                            onClick={(e) => { e.stopPropagation(); handleViewAvailability(car); }}
                          >
                            View Availability
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <AvailabilityModal
        isOpen={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        car={selectedCarForAvailability}
        upcomingReturns={selectedCarForAvailability?.upcomingReturns || []}
      />
      
      <Footer />
    </>
  );
};

export default Cars;
